import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenCheckIcon, CheckCircle2Icon, ClipboardCheckIcon, MessageSquareTextIcon, TimerIcon, XIcon } from 'lucide-react';
type Volume = {
  color: string;
  label: string;
  title: string;
  description: string;
  icon: string;
};
const volumes: Volume[] = [{
  color: 'bg-navy-800',
  label: 'VOLUME 01',
  title: 'A1 Foundations',
  description: 'Greetings, personal information, classroom language, and essential grammar.',
  icon: '1'
}, {
  color: 'bg-volume-teal',
  label: 'VOLUME 02',
  title: 'A1 Everyday Survival',
  description: 'Daily routines, food, shopping, directions, and real-world vocabulary.',
  icon: '2'
}, {
  color: 'bg-gold-500',
  label: 'VOLUME 03',
  title: 'A2 Confidence Builder',
  description: 'Past events, comparisons, future plans, jobs, technology, and advice.',
  icon: '3'
}, {
  color: 'bg-volume-orange',
  label: 'VOLUME 04',
  title: 'A2 Real-World Communication',
  description: 'Travel, interviews, phone calls, polite language, and practical scenarios.',
  icon: '4'
}, {
  color: 'bg-volume-red',
  label: 'VOLUME 05',
  title: 'B1 Fluency Foundations',
  description: 'Opinions, social issues, media, goals, and more fluent communication.',
  icon: '5'
}, {
  color: 'bg-volume-purple',
  label: 'VOLUME 06',
  title: 'B1 Independent Communicator',
  description: 'Presentations, formal writing, workplace English, reviews, and projects.',
  icon: '6'
}];
const benefits = [[ClipboardCheckIcon, 'Complete lesson plans', 'Every lesson comes organized from warm-up to wrap-up.'], [TimerIcon, 'Teacher guidance', 'Step-by-step instructions that make preparation effortless.'], [MessageSquareTextIcon, 'All four skills', 'Speaking, reading, writing, and listening in every course.'], [BookOpenCheckIcon, 'Ready for class', 'Grammar, vocabulary, assessments, and answer keys included.']];
export function MethodologySections() {
  return <>
      <section className="bg-white px-4 py-20 sm:py-28" data-id="element-98">
        <div className="mx-auto max-w-6xl" data-id="element-99">
          <div className="mx-auto max-w-3xl text-center" data-id="element-100">
            <div className="section-rule mx-auto" data-id="element-101" />
            <p className="mt-5 text-xs font-extrabold tracking-[.16em] text-gold-500" data-id="element-102">
              BUILT FOR REAL TEACHING DAYS
            </p>
            <h2 className="mt-4 font-display text-3xl uppercase leading-tight text-navy-950 sm:text-5xl" data-id="element-103">
              Stop building every lesson{' '}
              <span className="text-gold-500" data-id="element-104">from scratch.</span>
            </h2>
            <p className="mt-5 text-base leading-7 text-slate-600" data-id="element-105">
              Sunday night should not be your planning deadline. ESL Teacher
              Ready gives you the structure, progression, and classroom-ready
              materials to teach with certainty.
            </p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-2" data-id="element-106">
            <div className="rounded-3xl bg-navy-950 p-7 text-white sm:p-9" data-id="element-107">
              <h3 className="font-display text-2xl uppercase leading-tight" data-id="element-108">
                Does this sound familiar?
              </h3>
              <p className="mt-5 leading-7 text-blue-100" data-id="element-109">
                Tomorrow you teach an English class, but you are still opening
                three websites, collecting activities, and trying to make
                grammar, speaking, and writing fit into one coherent lesson.
              </p>
              <p className="mt-5 font-bold text-gold-300" data-id="element-110">
                Not because you do not know how to teach English. Because you
                were never given a complete ready-to-teach system.
              </p>
            </div>
            <div className="space-y-3 rounded-3xl border border-[#e6d9b7] bg-ivory p-7 sm:p-9" data-id="element-111">
              {['Nights lost searching for activities that fit your students’ level.', 'Lessons that feel repetitive because you fall back on what you know.', 'Improvisation that quietly chips away at teacher confidence.', 'No clear progression across weeks, so motivation fades.', 'Weekends spent planning instead of resting and living.'].map(item => <p key={item} className="flex gap-3 text-sm leading-6 text-slate-700" data-id="element-112">
                  <XIcon size={18} className="mt-0.5 shrink-0 text-volume-red" data-id="element-113" />
                  {item}
                </p>)}
            </div>
          </div>
          <p className="mx-auto mt-10 max-w-3xl text-center font-display text-2xl uppercase leading-tight text-navy-950" data-id="element-114">
            Plan more than a full year of English classes{' '}
            <span className="text-gold-500" data-id="element-115">starting today.</span>
          </p>
        </div>
      </section>

      <section className="bg-navy-950 px-4 py-20 text-white sm:py-28" data-id="element-116">
        <div className="mx-auto max-w-6xl" data-id="element-117">
          <div className="mx-auto max-w-3xl text-center" data-id="element-118">
            <p className="text-xs font-extrabold tracking-[.16em] text-gold-300" data-id="element-119">
              ONE SYSTEM. SIX POWERFUL RESOURCES.
            </p>
            <h2 className="mt-4 font-display text-3xl uppercase leading-tight sm:text-5xl" data-id="element-120">
              Everything you need to teach{' '}
              <span className="text-gold-300" data-id="element-121">with confidence.</span>
            </h2>
            <p className="mt-5 text-blue-100" data-id="element-122">
              120 complete lessons from A1 to B1 — with clear progression,
              teacher guidance, activities, assessments, and answers.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-123">
            {benefits.map(([Icon, title, text]) => {
            const BenefitIcon = Icon as typeof ClipboardCheckIcon;
            return <article key={title} className="rounded-2xl border border-white/15 bg-white/10 p-6" data-id="element-124">
                  <BenefitIcon size={28} className="text-gold-300" data-id="element-125" />
                  <h3 className="mt-5 text-lg font-extrabold" data-id="element-126">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100" data-id="element-127">{text}</p>
                </article>;
          })}
          </div>
        </div>
      </section>

      <section className="paper-grid bg-ivory px-4 py-20 sm:py-28" data-id="element-128">
        <div className="mx-auto max-w-6xl" data-id="element-129">
          <div className="text-center" data-id="element-130">
            <div className="section-rule mx-auto" data-id="element-131" />
            <p className="mt-5 text-xs font-extrabold tracking-[.16em] text-gold-500" data-id="element-132">
              THE COMPLETE PROGRESSION
            </p>
            <h2 className="mt-4 font-display text-3xl uppercase leading-tight text-navy-950 sm:text-5xl" data-id="element-133">
              Six volumes.{' '}
              <span className="text-gold-500" data-id="element-134">One clear path.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-slate-600" data-id="element-135">
              A premium, classroom-ready learning journey that takes students
              from foundational A1 skills to independent B1 communication.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-136">
            {volumes.map((volume, index) => <motion.article key={volume.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.07
          }} className="overflow-hidden rounded-2xl bg-white shadow-card" data-id="element-137">
                <div className={`${volume.color} flex h-24 items-center justify-between px-6 text-white`} data-id="element-138">
                  <div data-id="element-139">
                    <p className="text-xs font-extrabold tracking-[.14em] text-white/80" data-id="element-140">
                      {volume.label}
                    </p>
                    <h3 className="mt-1 text-lg font-extrabold" data-id="element-141">
                      {volume.title}
                    </h3>
                  </div>
                  <span className="text-4xl font-black text-white/85" data-id="element-142">
                    {volume.icon}
                  </span>
                </div>
                <p className="p-6 text-sm leading-6 text-slate-600" data-id="element-143">
                  {volume.description}
                </p>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-20 sm:py-28" data-id="element-144">
        <div className="mx-auto max-w-6xl" data-id="element-145">
          <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-center" data-id="element-146">
            <div data-id="element-147">
              <p className="text-xs font-extrabold tracking-[.16em] text-gold-500" data-id="element-148">
                DESIGNED FOR ESL & EFL TEACHERS
              </p>
              <h2 className="mt-4 font-display text-3xl uppercase leading-tight text-navy-950 sm:text-5xl" data-id="element-149">
                A complete system, not a random activity library.
              </h2>
              <p className="mt-6 text-base leading-7 text-slate-600" data-id="element-150">
                Teach engaging English lessons without spending your weekends
                building them. Every resource is designed around real classroom
                outcomes, real levels, and real teacher time.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-151">
              <div className="rounded-2xl border border-[#e6d9b7] bg-ivory p-6" data-id="element-152">
                <CheckCircle2Icon className="text-gold-500" data-id="element-153" />
                <p className="mt-4 font-extrabold text-navy-950" data-id="element-154">
                  A1, A2 & B1 structure
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-155">
                  A natural progression that helps students feel visible
                  progress.
                </p>
              </div>
              <div className="rounded-2xl border border-[#e6d9b7] bg-ivory p-6" data-id="element-156">
                <CheckCircle2Icon className="text-volume-teal" data-id="element-157" />
                <p className="mt-4 font-extrabold text-navy-950" data-id="element-158">
                  Classroom-ready resources
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-159">
                  Open the lesson, teach the lesson, and focus on your students.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}